<template>
  <div class="profile-page">
    <v-container
      fluid
      class="pa-0"
    >
      <v-row justify="center">
        <v-col
          cols="12"
          md="10"
          lg="8"
        >
          <v-card class="profile-container">
            <v-card-text class="pa-8">
              <!-- Header -->
              <h1 class="text-h3 font-weight-bold primary--text mb-8">{{ $t('shop.userDetails.title') }}</h1>

              <!-- Profile Header Section -->
              <div class="profile-header-section mb-10">
                <v-row
                  align="center"
                  justify="center"
                  justify-md="start"
                >
                  <v-col
                    cols="auto"
                    class="text-center"
                  >
                    <v-hover v-slot="{ hover }">
                      <v-badge
                        bottom
                        right
                        offset-x="30"
                        offset-y="30"
                        color="primary"
                        :value="hover"
                      >
                        <template v-slot:badge>
                          <v-icon small>mdi-upload</v-icon>
                        </template>
                        <v-avatar
                          size="120"
                          class="profile-avatar"
                          @click="$refs.profileImgInput.$refs.input.click()"
                        >
                          <v-img
                            :key="avatarKey"
                            :src="imageSource(oneUser.image, 250, 250)"
                            :alt="$t('shop.userDetails.alt.profileImage')"
                          >
                            <template v-slot:placeholder>
                              <v-row
                                class="fill-height ma-0"
                                align="center"
                                justify="center"
                              >
                                <v-icon
                                  size="60"
                                  color="grey lighten-1"
                                >
                                  mdi-account-circle
                                </v-icon>
                              </v-row>
                            </template>
                          </v-img>
                        </v-avatar>
                        <v-file-input
                          ref="profileImgInput"
                          class="d-none"
                          prepend-icon=""
                          hide-input
                          accept="image/*"
                          v-model="profileImg"
                          @change="profilBildUpload()"
                        />
                      </v-badge>
                    </v-hover>
                  </v-col>

                  <v-col
                    cols="12"
                    md=""
                    class="text-center text-md-left"
                  >
                    <div class="profile-info">
                      <h2 class="text-h4 font-weight-bold mb-2">
                        {{ oneUser?.name }}
                      </h2>
                      <p class="text-body-1 grey--text text--darken-2 mb-3">
                        {{ get("auth_user").email }}
                      </p>
                      <v-chip
                        :color="currentUser(['ROLE_END_USER']) ? 'primary' : 'secondary'"
                        dark
                        label
                        small
                      >
                        <v-icon
                          small
                          left
                        >
                          {{ currentUser(['ROLE_END_USER']) ? 'mdi-account' : 'mdi-shield-account' }}
                        </v-icon>
                        {{ currentUser(["ROLE_END_USER"]) ? $t('shop.userDetails.role.user') : $t('shop.userDetails.role.admin') }}
                      </v-chip>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- Personal Data Section -->
              <section class="data-section">
                <h2 class="section-title">{{ $t('shop.userDetails.section.personalData') }}</h2>

                <!-- Name -->
                <div class="data-row">
                  <span class="data-label">{{ $t('shop.userDetails.label.name') }}</span>
                  <div class="data-value">
                    <span class="mr-3">{{ oneUser?.name }}</span>
                    <v-dialog
                      v-model="userNameDialog"
                      max-width="400"
                      persistent
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          text
                          small
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          class="pa-0"
                        >
                          <v-icon
                            small
                            left
                          >mdi-pencil
                          </v-icon>
                          {{ $t('shop.userDetails.button.edit') }}
                        </v-btn>
                      </template>
                      <v-card outlined>
                        <v-card-title class="text-h6">
                          {{ $t('shop.userDetails.dialog.nameTitle') }}
                        </v-card-title>
                        <v-card-text class="mb-n4 pb-0">
                          <v-form
                            ref="nameForm"
                            v-model="valid.name"
                            @submit.prevent="updateName"
                          >
                            <v-text-field
                              v-model="tempValues.name"
                              :label="$t('shop.userDetails.label.fullName')"
                              outlined
                              dense
                              :rules="userNameRules"
                              autofocus
                            />
                          </v-form>
                        </v-card-text>
                        <v-card-actions class="d-flex justify-end mb-2 mr-2">
                          <v-btn
                            text
                            @click="cancelDialogFunc('userNameDialog')"
                          >
                            {{ $t('shop.userDetails.button.cancel') }}
                          </v-btn>
                          <v-btn
                            color="green"
                            text
                            :disabled="!valid.name"
                            @click="updateName"
                          >
                            {{ $t('shop.userDetails.button.save') }}
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                  </div>
                </div>

                <!-- Email -->
                <div class="data-row">
                  <span class="data-label">{{ $t('shop.userDetails.label.email') }}</span>
                  <div class="data-value">
                    <span class="mr-3">{{ get("auth_user").email }}</span>
                    <v-dialog
                      v-model="userMailDialog"
                      max-width="400"
                      persistent
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          text
                          small
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          class="pa-0"
                        >
                          <v-icon
                            small
                            left
                          >mdi-pencil</v-icon>
                          {{ $t('shop.userDetails.button.change') }}
                        </v-btn>
                      </template>
                      <v-card outlined>
                        <v-card-title class="text-h6">
                          {{ $t('shop.userDetails.dialog.emailTitle') }}
                        </v-card-title>
                        <v-card-text class="mb-n4 pb-0">
                          <v-form
                            ref="emailForm"
                            v-model="valid.email"
                            @submit.prevent="updateEmail"
                          >
                            <v-text-field
                              v-model="tempValues.email"
                              :label="$t('shop.userDetails.label.emailAddress')"
                              outlined
                              dense
                              :rules="eMailRules"
                              autofocus
                            />
                          </v-form>
                        </v-card-text>
                        <v-card-actions class="d-flex justify-end mb-2 mr-2">
                          <v-spacer />
                          <v-btn
                            text
                            @click="cancelDialogFunc('userMailDialog')"
                          >
                            {{ $t('shop.userDetails.button.cancel') }}
                          </v-btn>
                          <v-btn
                            color="green"
                            text
                            :disabled="!valid.email"
                            @click="updateEmail"
                          >
                            {{ $t('shop.userDetails.button.save') }}
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                  </div>
                </div>

                <!-- Password -->
                <div class="data-row">
                  <span class="data-label">{{ $t('shop.userDetails.label.password') }}</span>
                  <div class="data-value">
                    <span class="mr-3 password-dots">{{ $t('shop.userDetails.passwordMask') }}</span>
                    <v-dialog
                      v-model="userPWDialog"
                      max-width="400"
                      persistent
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          text
                          small
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          class="pa-0"
                        >
                          <v-icon
                            small
                            left
                          >mdi-pencil</v-icon>
                          {{ $t('shop.userDetails.button.edit') }}
                        </v-btn>
                      </template>
                      <v-card outlined>
                        <v-card-title class="text-h6">
                          {{ $t('shop.userDetails.dialog.passwordTitle') }}
                        </v-card-title>
                        <v-card-text class="mb-n4 pb-0">
                          <v-form
                            ref="passwordForm"
                            v-model="valid.password"
                            @submit.prevent="updatePassword"
                          >
                            <v-text-field
                              v-model="password"
                              :label="$t('shop.userDetails.label.newPassword')"
                              outlined
                              dense
                              :type="showPwIcon ? 'text' : 'password'"
                              :append-icon="showPwIcon ? 'mdi-eye' : 'mdi-eye-off'"
                              @click:append="showPwIcon = !showPwIcon"
                              :rules="passwordRules"
                              autofocus
                            />
                          </v-form>
                        </v-card-text>
                        <v-card-actions class="d-flex justify-end mb-2 mr-2">
                          <v-spacer />
                          <v-btn
                            text
                            @click="cancelDialogFunc('userPWDialog')"
                          >
                            {{ $t('shop.userDetails.button.cancel') }}
                          </v-btn>
                          <v-btn
                            color="primary"
                            text
                            :disabled="!valid.password"
                            @click="updatePassword"
                          >
                            {{ $t('shop.userDetails.button.save') }}
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                  </div>
                </div>

                <!-- Birthday -->
                <div class="data-row">
                  <span class="data-label">{{ $t('shop.userDetails.label.birthday') }}</span>
                  <div class="data-value">
                    <span class="mr-3">{{ formDate(oneUser.birthday) }}</span>
                    <v-dialog
                      v-model="userBirthDayDialog"
                      max-width="400"
                      persistent
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          text
                          small
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          class="pa-0"
                        >
                          <v-icon
                            small
                            left
                          >mdi-pencil</v-icon>
                          {{ $t('shop.userDetails.button.change') }}
                        </v-btn>
                      </template>
                      <v-card outlined>
                        <v-card-title class="text-h6">
                          {{ $t('shop.userDetails.dialog.birthdayTitle') }}
                        </v-card-title>
                        <v-card-text class="mb-n4 pb-0">
                          <v-form
                            ref="birthdayForm"
                            v-model="valid.birthdate"
                            @submit.prevent="submitBirthday"
                          >
                            <v-menu
                              ref="birthdateMenu"
                              v-model="birthdateMenu"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              min-width="auto"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  v-model="birthday"
                                  :label="$t('shop.userDetails.label.birthday')"
                                  prepend-icon="mdi-calendar"
                                  readonly
                                  outlined
                                  dense
                                  v-bind="attrs"
                                  v-on="on"
                                  :rules="birthdayRules"
                                />
                              </template>
                              <v-date-picker
                                v-model="birthday"
                                :active-picker.sync="activePicker"
                                min="1910-01-01"
                                :max="new Date().toISOString().substr(0, 10)"
                                @change="birthdateMenu = false"
                                locale="de"
                              />
                            </v-menu>
                          </v-form>
                        </v-card-text>
                        <v-card-actions class="d-flex justify-end mb-2 mr-2">
                          <v-spacer />
                          <v-btn
                            text
                            @click="cancelDialogFunc('userBirthDayDialog')"
                          >
                            {{ $t('shop.userDetails.button.cancel') }}
                          </v-btn>
                          <v-btn
                            color="green"
                            text
                            :disabled="!valid.birthdate"
                            @click="submitBirthday"
                          >
                            {{ $t('shop.userDetails.button.save') }}
                          </v-btn>
                        </v-card-actions>
                        <!-- Active Since -->
                      </v-card>
                    </v-dialog>
                  </div>
                </div>
                <div class="data-row">
                  <span class="data-label">{{ $t('shop.userDetails.label.activeSince') }}</span>
                  <span class="data-value">
                    {{ formDate(get("user").created) }}
                  </span>
                </div>
              </section>

              <!-- Subscription Section -->
              <section class="data-section">
                <h2 class="section-title">{{ $t('shop.userDetails.section.subscription') }}</h2>

                <p v-if="!subscriptionRows.length" class="text-body-2 mb-0">
                  {{ $t('shop.userDetails.subscription.empty') }}
                </p>

                <div v-for="sub in subscriptionRows" :key="sub.id" class="subscription-entry mb-6">
                  <div class="data-row">
                    <span class="data-label">{{ $t('shop.userDetails.label.plan') }}</span>
                    <div class="data-value">
                      <span class="mr-3">{{ sub.plan ? sub.plan.name : $t('shop.userDetails.value.unknown') }}</span>
                      <v-chip small :color="stateColor(sub.status)" text-color="white">
                        {{ stateLabel(sub.status) }}
                      </v-chip>
                    </div>
                  </div>

                  <div class="data-row">
                    <span class="data-label">{{ $t('shop.userDetails.label.accessUntil') }}</span>
                    <div class="data-value">
                      <span class="mr-3">{{ formDate(sub.period_end) }}</span>
                      <span v-if="remainingDays(sub.period_end) !== null" class="grey--text">
                        {{ $t('shop.userDetails.value.remainingDays', { days: remainingDays(sub.period_end) }) }}
                      </span>
                    </div>
                  </div>

                  <div class="data-row" v-if="sub.status !== 'cancelled' && sub.status !== 'expired'">
                    <span class="data-label">{{ $t('shop.userDetails.label.nextBilling') }}</span>
                    <div class="data-value">{{ formDate(sub.period_end) }}</div>
                  </div>

                  <div class="data-row">
                    <span class="data-label">{{ $t('shop.userDetails.label.cancelledAt') }}</span>
                    <div class="data-value">{{ formDate(sub.cancelled_at) }}</div>
                  </div>

                  <div class="data-row">
                    <span class="data-label">{{ $t('shop.userDetails.label.amount') }}</span>
                    <div class="data-value">
                      <span class="mr-3">{{ planAmount(sub) }}</span>
                      <span v-if="sub.plan" class="grey--text">{{ sub.plan.period }}</span>
                    </div>
                  </div>

                  <div class="data-row" v-if="sub.payment && sub.payment.card_type">
                    <span class="data-label">{{ $t('shop.userDetails.label.paymentMethod') }}</span>
                    <div class="data-value">{{ sub.payment.card_type }} **** {{ sub.payment.last4 }}</div>
                  </div>

                  <div class="mt-2">
                    <v-btn v-if="canCancel(sub)" text color="primary" @click="askCancel(sub.id)">
                      {{ $t('shop.userDetails.button.cancelSubscription') }}
                    </v-btn>
                    <v-btn v-if="canReactivate(sub)" text color="primary" @click="reactivateSubscription(sub.id)">
                      {{ $t('shop.userDetails.button.reactivateSubscription') }}
                    </v-btn>
                  </div>
                </div>

                <v-dialog v-model="cancelSubscriptionDialog" max-width="420">
                  <v-card>
                    <v-card-title>{{ $t('shop.userDetails.dialog.cancelSubscriptionTitle') }}</v-card-title>
                    <v-card-text>{{ $t('shop.userDetails.dialog.cancelSubscriptionBody') }}</v-card-text>
                    <v-card-actions>
                      <v-btn text @click="cancelSubscriptionDialog = false">
                        {{ $t('shop.userDetails.button.stay') }}
                      </v-btn>
                      <v-btn text color="primary" @click="confirmCancel">
                        {{ $t('shop.userDetails.button.confirmCancel') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </section>

              <section class="data-section">
                <h2 class="section-title">{{ $t('shop.userDetails.section.purchases') }}</h2>

                <p v-if="!purchaseRows.length" class="text-body-2 mb-0">
                  {{ $t('shop.userDetails.purchases.empty') }}
                </p>

                <div v-for="order in purchaseRows" :key="order.id" class="purchase-entry mb-6">
                  <div class="data-row">
                    <span class="data-label">{{ $t('shop.userDetails.label.date') }}</span>
                    <div class="data-value">
                      <span class="mr-3">{{ formDate(order.created) }}</span>
                      <v-chip small :color="purchaseColor(order)" text-color="white">
                        {{ purchaseLabel(order) }}
                      </v-chip>
                    </div>
                  </div>

                  <div class="data-row">
                    <span class="data-label">{{ $t('shop.userDetails.label.amount') }}</span>
                    <div class="data-value">{{ formatAmount(order.amount) }}</div>
                  </div>

                  <div class="data-row">
                    <span class="data-label">{{ $t('shop.userDetails.label.content') }}</span>
                    <div class="data-value">{{ purchaseTitles(order) }}</div>
                  </div>

                  <div class="data-row">
                    <span class="data-label">{{ $t('shop.userDetails.label.receipt') }}</span>
                    <div class="data-value">{{ order.order_id }}</div>
                  </div>
                </div>
              </section>


            </v-card-text>
          </v-card>
        </v-col>
      </v-row>


    </v-container>
  </div>
</template>

<script>
import userRole from "@/services/userRoleService";
import { mapGetters } from "vuex";
import APIService from "@/services/api.service";
import AppService from "@/services/app.service";
import moment from "moment";

export default {
  name: "UserProfile",

  data() {
    return {
      // Dialog states
      userNameDialog: false,
      userMailDialog: false,
      userPWDialog: false,
      userBirthDayDialog: false,
      cancelDialog: false,
      reactivateDialog: false,
      cancelSubscriptionDialog: false,
      pendingCancelId: null,

      // Date picker
      birthdateMenu: false,
      birthday: "",
      activePicker: null,

      // Form values
      password: "",
      profileImg: null,
      avatarKey: 0,
      showPwIcon: false,

      // Temporary values for dialogs
      tempValues: {
        name: "",
        email: "",
      },

      // Validation states
      valid: {
        email: false,
        password: false,
        name: false,
        birthdate: false,
      },

      // Validation rules
      eMailRules: [
        (v) => !!v || this.$t("shop.userDetails.validation.emailRequired"),
        (v) =>
          !v ||
          /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
          this.$t("shop.userDetails.validation.emailFormat"),
      ],

      passwordRules: [
        (v) => !!v || this.$t("shop.userDetails.validation.passwordRequired"),
        (v) => (v && v.length >= 6) || this.$t("shop.userDetails.validation.passwordMinLength"),
      ],

      userNameRules: [
        (v) => !!v || this.$t("shop.userDetails.validation.nameRequired"),
        (v) =>
          (v && v.length <= 30) ||
          this.$t("shop.userDetails.validation.nameMaxLength"),
        (v) =>
          (v && v.length >= 2) || this.$t("shop.userDetails.validation.nameMinLength"),
      ],
    };
  },

  computed: {
    ...mapGetters(["get"]),

    oneUser() {
      return this.$store.state.main?.user?.result || {};
    },

    authUser() {
      return this.$store.state.main?.auth_user?.result || {};
    },

    subscriptionRows() {
      const rows = this.oneUser?.subscriptions || [];
      return [...rows].sort((a, b) => Number(b.id) - Number(a.id));
    },

    purchaseRows() {
      const rows = this.oneUser?.orders || [];
      return [...rows].sort((a, b) => String(b.created).localeCompare(String(a.created)));
    },

    birthdayRules() {
      return [
        (v) => !!v || this.$t("shop.userDetails.validation.birthdayRequired"),
        (v) => {
          if (!v) return true;
          const birthDate = new Date(v);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
          ) {
            age--;
          }
          return age >= 18 || this.$t("shop.userDetails.validation.ageMinimum");
        },
      ];
    },
  },

  watch: {
    birthdateMenu(val) {
      val && setTimeout(() => (this.activePicker = "YEAR"));
    },
    userNameDialog(val) {
      if (val) {
        this.tempValues.name = this.oneUser.name;
      }
    },
    userMailDialog(val) {
      if (val) {
        this.tempValues.email = this.get("auth_user").email;
      }
    },
  },

  methods: {
    // Utility methods
    currentUser(roles) {
      return userRole.hasRole(roles);
    },

    formDate(value) {
      if (value) {
        return moment(String(value)).format("DD.MM.YYYY");
      }
      return "—";
    },

    // Betraege sind netto gespeichert. Die Anzeige rechnet 19 Prozent auf
    // (Regel von Kevin 2026-10-02).
    formatAmount(value) {
      const gross = Number(value) * 1.19;
      return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" })
        .format(isFinite(gross) ? gross : 0);
    },

    planAmount(sub) {
      const price = sub && sub.plan ? sub.plan.price : null;
      return price === null || price === undefined ? "—" : this.formatAmount(price);
    },

    remainingDays(value) {
      if (!value) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.ceil((new Date(value) - today) / 86400000);
      return days > 0 ? days : null;
    },

    isRunning(sub) {
      return !!sub && sub.status === "active" && !sub.cancelled_at;
    },

    canCancel(sub) {
      return this.isRunning(sub);
    },

    canReactivate(sub) {
      if (!sub) return false;
      const inactive = sub.status === "cancelled" || sub.status === "suspended";
      return inactive && !AppService.isSubscriptionUsable(sub);
    },

    askCancel(subId) {
      this.pendingCancelId = subId;
      this.cancelSubscriptionDialog = true;
    },

    confirmCancel() {
      this.cancelSubscriptionDialog = false;
      if (this.pendingCancelId) {
        this.cancelSubscription(this.pendingCancelId);
      }
    },

    stateLabel(status) {
      const key = "shop.userDetails.state." + String(status || "").toLowerCase();
      const label = this.$t(key);
      return label === key ? String(status || "—") : label;
    },

    stateColor(status) {
      if (status === "active") return "green";
      if (status === "cancelled") return "orange";
      if (status === "scheduled") return "blue";
      if (status === "pending" || status === "past_due") return "amber";
      return "grey";
    },

    purchaseTitles(order) {
      const videos = (order.videos || []).map((video) => video.title);
      const images = (order.images || []).map((image) => image.title);
      const titles = videos.concat(images).filter((title) => !!title);
      return titles.length ? titles.join(", ") : "—";
    },

    purchaseLabel(order) {
      if (order.failureReason) return this.$t("shop.userDetails.purchaseState.failed");
      if (order.order_paid === true) return this.$t("shop.userDetails.purchaseState.paid");
      if (order.order_paid === false) return this.$t("shop.userDetails.purchaseState.unpaid");
      return this.$t("shop.userDetails.purchaseState.unknown");
    },

    purchaseColor(order) {
      if (order.failureReason) return "red";
      if (order.order_paid === true) return "green";
      if (order.order_paid === false) return "orange";
      return "grey";
    },

    getNextBillingDate(transactionDate, recurringStatus, recurringPeriod) {
      return AppService.getNextBillingDate(
        transactionDate,
        recurringStatus,
        recurringPeriod
      );
    },

    // Dialog management
    cancelDialogFunc(dialogName) {
      this[dialogName] = false;
      // Reset temp values
      if (dialogName === 'userNameDialog') {
        this.tempValues.name = this.oneUser.name;
      } else if (dialogName === 'userMailDialog') {
        this.tempValues.email = this.get("auth_user").email;
      } else if (dialogName === 'userPWDialog') {
        this.password = "";
      } else if (dialogName === 'userBirthDayDialog') {
        this.birthday = "";
      }
    },

    // Update methods
    updateName() {
      if (!this.valid.name) return;

      this.editUserProfile({ name: this.tempValues.name });
      this.userNameDialog = false;
    },

    updateEmail() {
      if (!this.valid.email) return;

      this.editUserMail(this.authUser.id, this.tempValues.email);
      this.userMailDialog = false;
    },

    updatePassword() {
      if (!this.valid.password) return;

      this.editUserPwd();
      this.userPWDialog = false;
      this.password = "";
    },

    submitBirthday() {
      if (!this.valid.birthdate) return;

      const today = new Date();
      const birthDate = new Date(this.birthday);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      if (age >= 18) {
        this.editUserProfile({ birthday: this.birthday + " 00:00:00" });
        this.userBirthDayDialog = false;
      } else {
        APIService.setResponse({
          message: this.$t("shop.userDetails.message.mustBe18"),
          color: "red",
        });
      }
    },

    editUserProfile(object) {
      if (this.authUser.email.length > 0) {
        this.$store
          .dispatch("patch", {
            endpoint: `/api/v1/users/${this.oneUser.id}`,
            event: object,
            message: false,
          })
          .then(() => {
            APIService.setResponse({
              message: this.$t("shop.userDetails.message.changesSaved"),
              color: "green",
            });
            this.$store.dispatch("fetch", {
              endpoint: `/api/v1/users/${this.oneUser.id}`,
              state: "user",
            });
          });
      }
    },

    editUserMail(id, email) {
      if (email && email.length > 0) {
        this.$store
          .dispatch("patch", {
            endpoint: `/api/v1/users/${id}`,
            event: { email: email },
            host: "getAuthHost",
            message: false,
          })
          .then(() => {
            APIService.setResponse({
              message: this.$t("shop.userDetails.message.emailSaved"),
              color: "success",
            });
            // Update local state
            this.$store.dispatch("fetch", {
              endpoint: `/api/v1/users/me`,
              host: "getAuthHost",
              state: "auth_user",
            });
          });
      }
    },

    editUserPwd() {
      if (this.password && this.password.length > 0) {
        const payload = {
          password: this.password,
          email: this.authUser.email
        };

        this.$store
          .dispatch("patch", {
            endpoint: `/api/v1/users/${this.authUser.id}`,
            event: payload,
            host: "getAuthHost",
            message: false,
          })
          .then(() => {
            APIService.setResponse({
              message: this.$t("shop.userDetails.message.passwordChanged"),
              color: "success",
            });
          });
      }
    },

    profilBildUpload() {
      if (!this.profileImg) return;

      this.$store
        .dispatch("uploadFiles", {
          endpoint: `/api/v1/users/upload`,
          files: { image: this.profileImg },
          metadata: {},
          message: false,
        })
        .then(() => {
          this.$store.dispatch("fetch", {
            endpoint: `/api/v1/users/${this.oneUser.id}`,
            state: "user",
          }).then(() => {
            this.avatarKey++;
          });
          APIService.setResponse({
            message: this.$t("shop.userDetails.message.profileImageUploaded"),
            color: "success",
          });
          this.profileImg = null;
        });
    },

    onBeforeDeleteImage: function (fileRecord) {
      var i = this.fileRecordsForUpload.indexOf(fileRecord);
      if (i !== -1) {
        this.fileRecordsForUpload.splice(i, 1);
      } else {
        if (confirm(this.$t("shop.userDetails.message.confirmDeleteImage"))) {
          this.$refs.logo.deleteFileRecord(fileRecord);
        }
      }
    },

    imageSource(user, width, height) {
      return AppService.imageSource(user, width, height);
    },

    certImageSource(file) {
      return APIService.getApiHost() + `/${file.path}`;
    },

    deleteCertImage(id) {
      this.$store.dispatch("delete", {
        endpoint: `/api/v1/files/${id}`,
      });
    },

    confirmCancelSubscription() {
      this.cancelSubscription(this.get('subscription').id);
      this.cancelDialog = false;
    },

    cancelSubscription(subId) {
      this.$store
        .dispatch("post", {
          endpoint: `/api/v1/subscriptions/${subId}/cancel`,
          message: false,
        })
        .then(() => {
          APIService.setResponse({
            message: this.$t("shop.userDetails.message.subscriptionCancelled"),
            color: "info",
          });
          // The route writes the state of the subscription. Show the new
          // status at once, then sync with the provider. The view needs no
          // page load.
          this.setSubscriptionStatus("cancelled");
          this.loadSubscriptionData();
        })
        .catch((error) => {
          APIService.setResponse({
            message: `${this.$t("shop.userDetails.message.cancelError")}: ${error.message}`,
            color: "error",
          });
        });
    },

    setSubscriptionStatus(status) {
      const subscription = this.get("subscription");
      if (!subscription || !subscription.id) return;
      this.$store.dispatch("set", {
        state: "subscription",
        data: {
          ...this.$store.state.subscription,
          result: { ...subscription, status: status },
        },
      });
    },

    confirmReactivateSubscription() {
      this.reactivateSubscription(this.get('subscription').id);
      this.reactivateDialog = false;
    },

    reactivateSubscription(subId) {
      this.$store
        .dispatch("patch", {
          endpoint: `/api/v1/subscriptions/${subId}/activate`,
          message: false,
        })
        .then(() => {
          APIService.setResponse({
            message: this.$t("shop.userDetails.message.subscriptionReactivated"),
            color: "info",
          });
          // The route writes the state and reads the provider. Show the new
          // status at once, then sync with the provider.
          this.setSubscriptionStatus("active");
          this.loadSubscriptionData();
        })
        .catch((error) => {
          APIService.setResponse({
            message: `${this.$t("shop.userDetails.message.reactivateError")}: ${error.message}`,
            color: "error",
          });
        });
    },

    loadSubscriptionData() {
      const subs = this.$store.state.main?.user?.result?.subscriptions;
      if (subs?.length > 0) {
        // Der laufende Zugang zaehlt: die nutzbare Zeile, sonst die juengste.
        // Die alte Regel nahm immer die erste Zeile. Nach einem Upgrade ist das
        // die gekuendigte, und das Profil zeigte sie mit "Reaktivieren".
        const usable = subs.filter((sub) => AppService.isSubscriptionUsable(sub));
        const pool = usable.length ? usable : subs;
        const current = pool.reduce((a, b) =>
          Number(a.id) >= Number(b.id) ? a : b
        );
        const userSubId = current.id;
        this.$store
          .dispatch("fetch", {
            endpoint: `/api/v1/subscriptions/${userSubId}/payment`,
            state: "subscription",
          })
          .then((response) => {
            this.$store.dispatch("setPayment", response.result.payment);
          })
          .catch((error) => {
            console.error("Error loading subscription:", error);
          });
      }
    },

    tryLoadSubscription() {
      if (this.$store.state.main?.user?.result) {
        this.loadSubscriptionData();
      } else {
        this.unwatchUser = this.$store.watch(
          (state) => state.main?.user?.result,
          (val) => {
            if (val) {
              this.loadSubscriptionData();
              this.unwatchUser && this.unwatchUser();
            }
          }
        );
      }
    },
  },

  mounted() {
    this.tryLoadSubscription();
    if (this.$store.state.main.user.result.subject) {
      this.$store.dispatch("fetch", {
        endpoint: `/api/v1/users/${this.$store.state.main.user.result.subject}`,
        state: "auth_user",
        host: "getAuthHost",
      });
    }

    // Initialize birthday if exists
    if (this.oneUser.birthday) {
      this.birthday = moment(this.oneUser.birthday).format("YYYY-MM-DD");
    }
  },

  beforeDestroy() {
    this.unwatchUser && this.unwatchUser();
  },
};
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
}

.profile-container {
  border-radius: 8px;
}

.profile-header-section {
  padding-bottom: 20px;
}

.profile-avatar {
  position: relative;
  cursor: pointer;
}

.profile-upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  cursor: pointer;
}

.data-section {
  margin-top: 40px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 12px;
  /* border-bottom: 1px solid #e0e0e0; */
}

.data-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid;
}

.data-row:last-child {
  border-bottom: none;
}

.data-label {
  font-weight: 600;
  color: #616161;
  flex: 0 0 30%;
}

.data-value {
  flex: 1;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.password-dots {
  font-family: monospace;
  letter-spacing: 2px;
}

.payment-method {
  display: flex;
  align-items: center;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .profile-header-section .v-row {
    flex-direction: column;
    text-align: center;
  }

  .profile-info {
    text-align: center;
    margin-top: 16px;
  }

  .data-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .data-label {
    margin-bottom: 8px;
  }

  .data-value {
    justify-content: flex-start;
    text-align: left;
  }
}

/* Vuetify overrides for cleaner look */

::v-deep .v-chip--label {
  border-radius: 4px !important;
}
</style>
